import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";
import { AppShellV8 } from "../AppShellV8";
import { getActiveClient, getCurrentMemberId } from "../activeClient";
import { EntityPicker } from "../EntityPicker";
import { personName, personColor, personInitials } from "../people";
import { splitAssets, replaceAsset } from "../assetSegments";
import { AssetDocument, rendererFor } from "../assetRenderers";
import { FriendlyLoading } from "../FriendlyLoading";

/**
 * Deliverable content view — the page a deliverable link (Reviews, Digest,
 * Campaigns, Knowledge Base) opens. A deliverable IS a KB doc, so this renders
 * the document's real markdown body plus its metadata, and exposes the same
 * review writes (Set for review / Approve / Request changes) as the Reviews queue.
 */

const CLIENT_SLUG = getActiveClient().slug;

const TYPE_LABEL: Record<string, string> = {
  email: "Email", page: "Page", social: "Social", report: "Report",
  doc: "Doc", ad: "Ad", script: "Script", data: "Data", config: "Config",
};

// Owner/person lookup is the shared, normalized one in ../people (AMB-341).

interface ApiDeliverable {
  id: string;
  title: string;
  deliverableType?: string;
  client?: string;
  campaign?: string | null;
  phase?: string | null;
  owner?: string | null;
  createdBy?: string | null;
  approvers?: string[];
  due?: string | null;
  difficulty?: number;
  status?: string | null;
  reviewState?: string | null;
  comments?: { author: string; at: string; text: string }[];
  body?: string;
  sourceMeetingDetails?: { id: string; title: string; date?: string | null; summaryPoints?: string[] }[];
  sourceAction?: string | null;
  sourceActionDetail?: { id: string; task: string } | null;
}

// D3 combined chip — first match wins.
function statusChip(status?: string | null, reviewState?: string | null): { label: string; cls: string } {
  if (status === "archived") return { label: "Archived", cls: "bg-gray-100 text-gray-500" };
  if (status === "blocked") return { label: "Blocked", cls: "bg-red-50 text-red-700" };
  if (reviewState === "rejected") return { label: "Rejected", cls: "bg-red-50 text-red-700" };
  if (reviewState === "changes-requested") return { label: "Changes Requested", cls: "bg-amber-50 text-amber-700" };
  if (reviewState === "in-review") return { label: "In Review", cls: "bg-amber-50 text-amber-700" };
  if (status === "deployed") return { label: "Deployed", cls: "bg-emerald-50 text-emerald-700" };
  if (status === "secured") return { label: "Secured", cls: "bg-emerald-50 text-emerald-700" };
  if (reviewState === "approved") return { label: "Approved", cls: "bg-emerald-50 text-emerald-700" };
  if (status === "drafted") return { label: "Draft", cls: "bg-gray-100 text-gray-600" };
  return { label: "To Draft", cls: "bg-gray-100 text-gray-500" };
}

const fmtDate = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export function TranscriptView() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const id = params.get("id");

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [doc, setDoc] = useState<ApiDeliverable | null>(null);
  const [reviewState, setReviewState] = useState<string>("none");
  const [busy, setBusy] = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const [linkingSource, setLinkingSource] = useState(false);
  const [campaignPicker, setCampaignPicker] = useState(false);
  const [campaigns, setCampaigns] = useState<Array<{ id: string; name: string; phases: Array<{ id: string; name: string }> }>>([]);

  // D4: link source meeting(s) onto this deliverable, then refetch.
  const linkSources = async (ids: string[]) => {
    if (!doc) return;
    const merged = Array.from(new Set([...(doc.sourceMeetingDetails?.map((m) => m.id) || []), ...ids]));
    await fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CLIENT_SLUG, id: doc.id, fields: { sourceMeetings: merged } }),
    }).catch(() => {});
    setLinkingSource(false);
    const fresh = await fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/${encodeURIComponent(doc.id)}?client=${CLIENT_SLUG}`).then((r) => r.json()).catch(() => null);
    if (fresh) setDoc(fresh);
  };

  // D5 author side: after addressing requested changes, send it back into review.
  const resubmitForReview = async () => {
    if (!doc) return;
    await fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CLIENT_SLUG, id: doc.id, fields: { reviewState: "in-review" } }),
    }).catch(() => {});
    const fresh = await fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/${encodeURIComponent(doc.id)}?client=${CLIENT_SLUG}`).then((r) => r.json()).catch(() => null);
    if (fresh) { setDoc(fresh); setReviewState(fresh.reviewState || "none"); }
  };

  // AMB-329: optional filing — set/change the campaign and/or phase, then refetch.
  const setCampaignField = async (fields: Record<string, unknown>) => {
    if (!doc) return;
    await fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CLIENT_SLUG, id: doc.id, fields }),
    }).catch(() => {});
    const fresh = await fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/${encodeURIComponent(doc.id)}?client=${CLIENT_SLUG}`).then((r) => r.json()).catch(() => null);
    if (fresh) setDoc(fresh);
  };

  // AMB-329: load campaigns once for the "add to campaign (+ phase)" control.
  useEffect(() => {
    fetch(`${DOCS_API_DOMAIN}/api/agents/campaigns?client=${CLIENT_SLUG}`)
      .then((r) => r.json())
      .then((data) => setCampaigns(data.items || []))
      .catch(() => setCampaigns([]));
  }, []);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/${encodeURIComponent(id)}?client=${CLIENT_SLUG}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: ApiDeliverable) => {
        if (cancelled) return;
        setDoc(d);
        setReviewState(d.reviewState || "none");
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setNotFound(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Review writes — same direct-write endpoint the Reviews queue uses.
  // Request changes WITH a required comment so the author gets actionable feedback
  // (the bare reviewState flip left them with no idea what to change).
  const requestChanges = () => {
    if (!doc) return;
    const text = window.prompt("What changes are needed? The author will see this.");
    if (text === null || !text.trim()) return;
    setBusy(true);
    setReviewState("changes-requested");
    const comments = [...(doc.comments || []), { author: getCurrentMemberId() ?? "", at: new Date().toISOString(), text: text.trim() }];
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CLIENT_SLUG, id: doc.id, fields: { reviewState: "changes-requested", comments } }),
    })
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); setDoc({ ...doc, reviewState: "changes-requested", comments }); setActionMsg("Changes requested"); })
      .catch(() => { setReviewState(doc.reviewState || "none"); setActionMsg("Couldn't save — try again."); })
      .finally(() => { setBusy(false); setTimeout(() => setActionMsg(null), 3000); });
  };

  const writeReview = (next: string, msg: string) => {
    if (!doc) return;
    setBusy(true);
    setReviewState(next); // optimistic
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CLIENT_SLUG, id: doc.id, reviewState: next }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        setActionMsg(msg);
      })
      .catch(() => {
        setReviewState(doc.reviewState || "none"); // roll back
        setActionMsg("Couldn't save — try again.");
      })
      .finally(() => {
        setBusy(false);
        setTimeout(() => setActionMsg(null), 3000);
      });
  };

  // Asset-only edit (D-5): if generation tagged the body with <asset> wrappers, edit ONLY
  // those block(s) — the instructions / Recommendation / Open-questions scaffolding stay
  // read-only and the frontmatter is hidden. A legacy (un-tagged) body still edits whole.
  const parsed = useMemo(() => splitAssets(doc?.body || ""), [doc?.body]);
  const [assetDrafts, setAssetDrafts] = useState<string[]>([]);

  const startEdit = () => {
    if (!doc) return;
    if (parsed.hasAssets) setAssetDrafts(parsed.assets.map((a) => a.content));
    else setDraft(doc.body || "");
    setEditing(true);
  };
  const saveContent = () => {
    if (!doc) return;
    setSaving(true);
    // Reconstruct the body from the original, replacing only the edited asset block(s) so
    // the scaffolding is preserved exactly. Legacy bodies save the whole textarea.
    let body = doc.body || "";
    if (parsed.hasAssets) assetDrafts.forEach((c, i) => { body = replaceAsset(body, i, c); });
    else body = draft;
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/content`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: CLIENT_SLUG, id: doc.id, body }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        setDoc((prev) => (prev ? { ...prev, body } : prev));
        setEditing(false);
        setActionMsg("Saved");
      })
      .catch(() => setActionMsg("Couldn't save — try again."))
      .finally(() => {
        setSaving(false);
        setTimeout(() => setActionMsg(null), 3000);
      });
  };

  if (loading) {
    return (
      <AppShellV8 breadcrumb={["Knowledge Base", "Document"]}>
        <div className="flex h-full items-center justify-center">
          <FriendlyLoading message="Opening the document…" />
        </div>
      </AppShellV8>
    );
  }

  if (notFound || !doc) {
    return (
      <AppShellV8 breadcrumb={["Knowledge Base", "Document"]}>
        <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
          <p className="text-[13px] text-gray-500">{id ? "This document couldn't be found." : "No document selected."}</p>
          <Link to="/agents/knowledge-base" className="rounded-lg bg-gray-900 px-4 py-1.5 text-[13px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97]">
            Back to Knowledge Base
          </Link>
        </div>
      </AppShellV8>
    );
  }

  const chip = statusChip(doc.status, reviewState);
  const ownerNm = personName(doc.owner);
  const createdByNm = doc.createdBy ? personName(doc.createdBy) : null;
  const inReview = reviewState === "in-review" || reviewState === "changes-requested";

  const headerActions = (
    <div className="flex shrink-0 items-center gap-2">
      {actionMsg && <span className="text-xs font-medium text-gray-400">{actionMsg}</span>}
      {editing ? (
        <>
          <button disabled={saving} onClick={() => setEditing(false)} className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-500 transition-all duration-150 hover:bg-gray-100 disabled:opacity-50">Cancel</button>
          <button disabled={saving} onClick={saveContent} className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97] disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </>
      ) : (
        <>
          <button
            data-testid="source-toggle"
            onClick={() => setShowSource((v) => !v)}
            className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium shadow-sm transition-all duration-150 active:scale-[0.97] ${showSource ? "border-blue-200 bg-blue-50 text-blue-700" : "border-black/[0.06] bg-white text-gray-600 hover:bg-gray-50"}`}
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" /></svg>
            Source ({doc.sourceMeetingDetails?.length || 0})
          </button>
          <button onClick={startEdit} className="rounded-lg border border-black/[0.06] bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-all duration-150 hover:bg-gray-50 active:scale-[0.97]">Edit</button>
        </>
      )}
      {!editing && !inReview && reviewState !== "approved" && (
        <button
          disabled={busy}
          onClick={() => writeReview("in-review", "Sent for review")}
          className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97] disabled:opacity-50"
        >
          Send for review
        </button>
      )}
      {!editing && inReview && (
        <button
          disabled={busy}
          onClick={requestChanges}
          className="rounded-lg border border-black/[0.06] bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-all duration-150 hover:bg-gray-50 active:scale-[0.97] disabled:opacity-50"
        >
          Request changes
        </button>
      )}
      {!editing && inReview && (
        <button
          disabled={busy}
          onClick={() => writeReview("approved", "Approved")}
          className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97] disabled:opacity-50"
        >
          Approve
        </button>
      )}
    </div>
  );

  return (
    <AppShellV8 breadcrumb={["Knowledge Base", doc.title]} headerActions={headerActions}>
      <div className="flex h-full flex-col overflow-auto">
        {/* Document header */}
        <div className="border-b border-black/[0.06] px-6 py-5">
          <div className="mb-2.5 flex items-center gap-2.5">
            <button onClick={() => navigate(-1)} className="rounded-lg p-1.5 text-gray-400 transition-all duration-150 hover:bg-gray-50 hover:text-gray-600" aria-label="Back">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
            </button>
            <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">{TYPE_LABEL[doc.deliverableType || "doc"] || "Doc"}</span>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${chip.cls}`}>{chip.label}</span>
          </div>
          <h1 className="text-xl font-bold tracking-tight text-gray-900">{doc.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-gray-400">
            {doc.sourceActionDetail && (() => {
              const fromMeetingId = (doc.sourceAction || "").replace(/-act-\d+$/, "") || doc.sourceMeetingDetails?.[0]?.id || "";
              return fromMeetingId ? (
                <Link to={`/agents/digest?id=${encodeURIComponent(fromMeetingId)}`} className="text-gray-500 hover:text-gray-700 hover:underline">
                  ↳ from: {doc.sourceActionDetail.task}
                </Link>
              ) : (
                <span className="text-gray-500">↳ from: {doc.sourceActionDetail.task}</span>
              );
            })()}
            {doc.campaign ? (
              <span className="flex items-center gap-2">
                <Link to={`/agents/campaigns?campaign=${encodeURIComponent(doc.campaign)}`} className="text-blue-600 transition-all duration-150 hover:underline">
                  Campaign: {doc.campaign.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                </Link>
                <button onClick={() => setCampaignPicker(true)} className="text-blue-600 hover:underline">Change</button>
                <select
                  value={doc.phase || ""}
                  onChange={(e) => setCampaignField({ phase: e.target.value || null })}
                  className="rounded-md border border-black/[0.06] bg-white px-1.5 py-0.5 text-xs text-gray-600"
                >
                  <option value="">Phase…</option>
                  {(campaigns.find((c) => c.id === doc.campaign)?.phases || []).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </span>
            ) : (
              <button onClick={() => setCampaignPicker(true)} className="rounded-md border border-black/[0.06] px-2 py-0.5 text-blue-600 hover:bg-blue-50">
                + Add to campaign
              </button>
            )}
            <span className="flex items-center gap-1.5">
              <span className={`flex h-5 w-5 items-center justify-center rounded-full ${personColor(doc.owner)} text-[7px] font-bold text-white shadow-sm`}>{personInitials(doc.owner)}</span>
              {ownerNm}
            </span>
            {createdByNm && <span>Created by {createdByNm}</span>}
            <span>Due {fmtDate(doc.due)}</span>
          </div>
        </div>

        {/* Document body + Source panel */}
        <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-auto px-6 py-6">
          {/* D5 author side: changes-requested banner with the reviewer's note + re-submit. */}
          {doc.reviewState === "changes-requested" && (
            <div data-testid="changes-banner" className="mx-auto mb-4 max-w-3xl rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-amber-800">Changes requested</p>
                  {doc.comments && doc.comments.length > 0 ? (
                    <p className="mt-1 text-[13px] text-amber-900">{doc.comments[doc.comments.length - 1].text}</p>
                  ) : (
                    <p className="mt-1 text-[12px] text-amber-700">The reviewer asked for changes. Make your edits, then re-submit.</p>
                  )}
                </div>
                <button data-testid="resubmit-review" onClick={resubmitForReview} className="shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-all hover:bg-amber-700">
                  Re-submit for review
                </button>
              </div>
            </div>
          )}
          <div className="mx-auto max-w-3xl rounded-xl bg-white p-8 shadow-sm ring-1 ring-black/[0.04]">
            {editing && parsed.hasAssets ? (
              <div className="space-y-3">
                <div className="rounded-lg bg-gray-50 px-3 py-2 text-[11px] text-gray-400 ring-1 ring-black/[0.04]">
                  You're editing just the asset{parsed.assets.length > 1 ? "s" : ""}. The instructions and recommendation stay as written, and the document's frontmatter is hidden.
                </div>
                {/* Typed per-asset editors (assetRenderers registry): default = textarea;
                    registered types (e.g. email) get their own editor. */}
                {parsed.assets.map((a, i) => {
                  const Editor = rendererFor(a.type).Editor;
                  return (
                    <Editor
                      key={i}
                      content={assetDrafts[i] ?? ""}
                      onChange={(v) => setAssetDrafts((prev) => prev.map((c, idx) => (idx === i ? v : c)))}
                      label={a.label || `Asset ${i + 1}`}
                      type={a.type}
                    />
                  );
                })}
              </div>
            ) : editing ? (
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="h-[60vh] w-full resize-y rounded-lg border border-black/[0.06] bg-white p-4 font-mono text-[12px] leading-relaxed text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                spellCheck={false}
              />
            ) : doc.body ? (
              // AssetDocument renders scaffolding as markdown + each <asset> through its typed
              // renderer (fixes raw block-markdown leaking inside the wrapper); tagless bodies
              // render as plain markdown.
              <AssetDocument body={doc.body} />
            ) : (
              <p className="text-[13px] text-gray-400">This document has no content yet.</p>
            )}
          </div>
        </div>
        {showSource && !editing && (
          <aside data-testid="source-panel" className="w-80 shrink-0 overflow-auto border-l border-black/[0.06] bg-gray-50/40 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-[13px] font-semibold text-gray-900">Source</h3>
              <button data-testid="source-link" onClick={() => setLinkingSource(true)} className="text-[11px] font-medium text-blue-600 hover:underline">+ Link a meeting</button>
            </div>
            {(doc.sourceMeetingDetails?.length || 0) === 0 ? (
              <p className="text-xs text-gray-400">No source meeting linked to this deliverable.</p>
            ) : (
              <div className="space-y-3">
                {doc.sourceMeetingDetails!.map((m) => (
                  <div key={m.id} className="rounded-lg border border-black/[0.06] bg-white p-3 shadow-sm">
                    <Link to={`/agents/digest?id=${encodeURIComponent(m.id)}`} className="text-[13px] font-semibold text-blue-600 transition-all duration-150 hover:underline">{m.title}</Link>
                    {m.date && <p className="mt-0.5 text-[11px] text-gray-400">{new Date(m.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>}
                    {(m.summaryPoints?.length || 0) > 0 && (
                      <ul className="mt-2 space-y-1.5">
                        {m.summaryPoints!.slice(0, 4).map((pt, i) => (
                          <li key={i} className="flex gap-2 text-[12px] leading-snug text-gray-600"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-300" />{pt}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </aside>
        )}
        </div>
      </div>
      {linkingSource && doc && (
        <EntityPicker
          title="Link a source meeting"
          endpoint={`/meetings?client=${CLIENT_SLUG}`}
          mapItem={(m) => ({ id: m.id, label: m.title || m.id, sublabel: m.date ? new Date(m.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : undefined })}
          multiple
          selected={doc.sourceMeetingDetails?.map((m) => m.id) || []}
          confirmLabel="Link meetings"
          onConfirm={linkSources}
          onClose={() => setLinkingSource(false)}
        />
      )}
      {campaignPicker && doc && (
        <EntityPicker
          title="Add to campaign"
          endpoint={`/campaigns?client=${CLIENT_SLUG}`}
          mapItem={(c) => ({ id: c.id, label: c.name || c.id })}
          selected={doc.campaign ? [doc.campaign] : []}
          confirmLabel="Set campaign"
          onConfirm={async (ids) => { setCampaignPicker(false); if (ids[0]) await setCampaignField({ campaign: ids[0] }); }}
          onClose={() => setCampaignPicker(false)}
        />
      )}
    </AppShellV8>
  );
}
